import { useEffect } from "react";
import { ArrowDown, ArrowRight } from "lucide-react";
import { PromptLibraryNavigation } from "@/components/PromptLibraryNavigation";
import { PromptLibraryAI30Link } from "@/components/PromptLibraryAI30";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { useTrackScrollDepth } from "@/hooks/use-posthog";
import { trackEvent } from "@/utils/posthog";
import { AIWorkflowLibrary } from "./AIWorkflowLibrary";
import { usePromptLibraryReferral } from "@/hooks/use-prompt-library-referral";
import { shareImage } from "@/utils/promptLibraryShare";
import { library } from "@/data/aiWorkflows";
import { LIBRARY_HELP_COPY } from "@/data/promptLibraryCopy";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { getOrganizationJourneyContext } from "@/utils/organizationJourney";

const Fortune100PromptLibrary = () => {
  const isOrganization = Boolean(getOrganizationJourneyContext(useHydratedSearch()));
  usePromptLibraryReferral();
  useTrackScrollDepth("Fortune 100 Prompt Library Page");
  useEffect(() => {
    trackEvent("Fortune 100 Prompt Library Page View", {
      has_strategy: true,
      has_cta: true,
    });
  }, []);
  return (
    <div className="min-h-screen bg-longhand-paper text-longhand-ink">
      <SEO
        robots="noindex, nofollow, noarchive, nosnippet"
        title={`Fortune 100 Prompt Library - ${library.length} Practical AI Workflows`}
        image={shareImage()}
        imageAlt={`${library.length} practical AI workflows. Choose a task, review a draft, and improve the next run.`}
        imageWidth={1200}
        imageHeight={630}
        description={`Put AI to work on something you already do. Pick one of ${library.length} workflows, add your notes or documents, and make a draft you can check and use.`}
        keywords={[
          "AI prompts",
          "ChatGPT prompts",
          "Claude skills",
          "AI workflows",
          "prompt templates",
          "fortune 100 prompts",
        ]}
      />
      <PromptLibraryNavigation />
      <main>
        <section
          aria-labelledby="library-hero-title"
          className="relative isolate overflow-hidden bg-background px-4 pb-8 pt-24 text-foreground sm:px-6 sm:pb-12 sm:pt-28 lg:px-8"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10"
          >
            <img
              src="/ai-in-ar.png"
              alt=""
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/70" />
          </div>
          <div className="mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-12">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-300">
                The Fortune 100 AI Prompt Library™
              </p>
              <h1
                id="library-hero-title"
                className="mt-4 text-balance font-serif text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
              >
                Put AI to work on{" "}
                <span className="text-blue-300">something you already do.</span>
              </h1>
              <p className="mt-4 max-w-2xl text-lg leading-relaxed text-foreground/80">
                {library.length} practical AI workflows for leaders and teams.
                Pick a task, add your notes or documents, and make a draft you
                can check and use.
              </p>
              <Button
                asChild
                className="mt-5 min-h-11 bg-longhand-accent text-longhand-paper hover:bg-longhand-accent-hover"
              >
                <a href="#workflow-search">
                  Find a workflow
                  <ArrowDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
            </div>
            <aside
              aria-labelledby="library-explainer-title"
              className="min-w-0 border-t border-foreground/20 pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0"
            >
              <h2
                id="library-explainer-title"
                className="font-serif text-xl font-semibold sm:text-2xl"
              >
                {LIBRARY_HELP_COPY.heading}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-foreground/80">
                {LIBRARY_HELP_COPY.description}
              </p>
              <p className="mt-3 text-base leading-relaxed text-foreground/80">
                {isOrganization ? "Want help putting this into practice across your team? Explore AI 30, or discuss the implementation support your organization needs." : LIBRARY_HELP_COPY.invitation}
              </p>
              <PromptLibraryAI30Link
                placement="hero"
                className="mt-3 bg-transparent px-0 text-blue-300 underline underline-offset-4 hover:bg-transparent hover:text-blue-200"
              >
                See how we help
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </PromptLibraryAI30Link>
            </aside>
          </div>
        </section>
        <AIWorkflowLibrary />
      </main>
    </div>
  );
};
export default Fortune100PromptLibrary;
